import moment from 'moment-timezone';
import { isDescriptionTooBig } from './checklistDescription';
import { toIsoOrNull } from './checklistSchedule';

export const newSection = () => ({
	id: crypto.randomUUID(),
	name: '',
	description: '',
	group_ids: [],
	appears_at: null,
	current_until: null,
	items: [],
});

export const newItem = () => ({
	id: crypto.randomUUID(),
	text: '',
	description: '',
	group_ids: [],
	on_press: null,
});

const toDraftItem = item => ({
	...item,
	text: item.text ?? '',
	description: item.description ?? '',
	group_ids: item.group_ids ?? [],
	on_press: item.on_press ?? null,
});

const toDraftSection = section => ({
	...section,
	name: section.name ?? '',
	description: section.description ?? '',
	group_ids: section.group_ids ?? [],
	appears_at: toIsoOrNull(section.appears_at),
	current_until: toIsoOrNull(section.current_until),
	items: (section.items ?? []).map(toDraftItem),
});

// The backend drops empty fields (omitempty), so stored content can lack group_ids, on_press or times.
export const toDraft = checklist => ({
	id: checklist?.id ?? null,
	version: checklist?.version ?? null,
	name: checklist?.name ?? '',
	content: { sections: (checklist?.content?.sections ?? []).map(toDraftSection) },
});

export const newDraft = () => ({
	id: null,
	version: null,
	name: '',
	content: { sections: [{ ...newSection(), items: [newItem()] }] },
});

const isFilled = text => !!text?.trim();

const plural = (count, word) => `${count} ${word}${count === 1 ? '' : 's'}`;

const agree = (count, singularVerb, pluralVerb) => (count === 1 ? singularVerb : pluralVerb);

const isBackwards = ({ appears_at, current_until }) =>
	!!appears_at && !!current_until && !moment(current_until).isAfter(appears_at);

export const draftProblem = draft => {
	if (!isFilled(draft.name)) return 'Name the checklist to save';
	const { sections } = draft.content;
	const unnamed = sections.filter(section => !isFilled(section.name)).length;
	if (unnamed) return `${plural(unnamed, 'section')} ${agree(unnamed, 'needs', 'need')} a name`;
	const items = sections.flatMap(section => section.items);
	const blank = items.filter(item => !isFilled(item.text)).length;
	if (blank) return `${plural(blank, 'item')} ${agree(blank, 'needs', 'need')} text`;
	const long = [...sections, ...items].filter(entry => isDescriptionTooBig(entry.description))
		.length;
	if (long) {
		const verb = agree(long, 'is too long or has', 'are too long or have');
		return `${plural(long, 'description')} ${verb} too many links`;
	}
	const backwards = sections.filter(isBackwards).length;
	if (backwards) {
		const moves = agree(
			backwards,
			'moves to past before it shows',
			'move to past before they show'
		);
		return `${plural(backwards, 'section')} ${moves} up`;
	}
	return null;
};

export const groupsLabel = (groups, groupIDs) => {
	if (!groupIDs.length) return 'Everyone';
	const names = groupIDs.map(id => groups.find(group => group.id === id)?.name ?? 'Deleted group');
	const shown = names.slice(0, 2).join(', ');
	return `${shown}${names.length > 2 ? ` +${names.length - 2}` : ''} only`;
};

export const checklistSize = content => {
	const sections = content?.sections ?? [];
	const items = sections.reduce((total, section) => total + section.items.length, 0);
	return `${plural(sections.length, 'section')}, ${plural(items, 'item')}`;
};

export const patchById = (list, id, patch) =>
	list.map(entry =>
		entry.id === id ? { ...entry, ...(typeof patch === 'function' ? patch(entry) : patch) } : entry
	);

export const withoutId = (list, id) => list.filter(entry => entry.id !== id);

export const itemIDs = draft =>
	new Set(draft.content.sections.flatMap(section => section.items.map(item => item.id)));

export const findItem = (sections, { sectionID, itemID }) =>
	sections.find(section => section.id === sectionID)?.items.find(item => item.id === itemID);
